import React, { useEffect, useState, FormEvent, ChangeEvent } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Box,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useIntl } from "react-intl";
import {
  BioFlowHandoffCredentials,
  isValidBioFlowPassword,
} from "src/types/bioflow";

type BioFlowPasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (credentials: BioFlowHandoffCredentials) => void | Promise<void>;
  isSubmitting?: boolean;
  /** When true, user already has a server-side account (resume onboarding). */
  isReturningUser?: boolean;
  /** After SalBioflow onboarding — set scan password before checkout. */
  variant?: "default" | "postOnboarding";
};

const BioFlowPasswordModal: React.FC<BioFlowPasswordModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  isReturningUser = false,
  variant = "default",
}) => {
  const postOnboarding = variant === "postOnboarding";
  const { formatMessage } = useIntl();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirm(false);
      setTouched(false);
    }
  }, [isOpen]);

  const passwordInvalid = touched && !isValidBioFlowPassword(password);
  const confirmInvalid =
    touched && (confirmPassword !== password || !confirmPassword);
  const canSubmit =
    isValidBioFlowPassword(password) &&
    confirmPassword === password &&
    !isSubmitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (
      !isValidBioFlowPassword(password) ||
      confirmPassword !== password ||
      isSubmitting
    ) {
      return;
    }
    await onConfirm({ password });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      isCentered
    >
      <ModalOverlay />
      <ModalContent width="90%" maxW="480px">
        <ModalHeader padding="20px 20px 0">
          {formatMessage({
            id: isReturningUser
              ? "bioflow.password.continueTitle"
              : "bioflow.password.title",
            defaultMessage: isReturningUser
              ? "Continue to SALBioFlow"
              : "Create your BioWell scan application password",
          })}
          <ModalCloseButton isDisabled={isSubmitting} />
        </ModalHeader>
        <ModalBody padding="20px 30px 30px">
          <Text mb={4} fontSize="sm" color="gray.600">
            {formatMessage({
              id: postOnboarding
                ? "bioflow.password.postOnboardingDescription"
                : isReturningUser
                  ? "bioflow.password.continueDescription"
                  : "bioflow.password.description",
              defaultMessage: postOnboarding
                ? "Choose a password for the BioWell mobile application, then continue to select your subscription plan."
                : isReturningUser
                  ? "Your SALBioFlow account is ready. Continue to complete onboarding or open the app."
                  : "Choose a password to use for your scans with the BioWell mobile application",
            })}
          </Text>
          <FormControl
            as="form"
            onSubmit={handleSubmit}
            isInvalid={passwordInvalid || confirmInvalid}
          >
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={passwordInvalid} isRequired>
                <InputGroup>
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    autoComplete="new-password"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setPassword(e.target.value)
                    }
                    placeholder={formatMessage({
                      id: "bioflow.password.placeholder",
                      defaultMessage: "Password",
                    })}
                    isDisabled={isSubmitting}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    />
                  </InputRightElement>
                </InputGroup>
                {passwordInvalid && (
                  <FormErrorMessage>
                    {formatMessage({
                      id: "bioflow.password.invalid",
                      defaultMessage:
                        "Password must be at least 6 characters and contain only Latin letters and numbers.",
                    })}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={confirmInvalid} isRequired>
                <InputGroup>
                  <Input
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    autoComplete="new-password"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setConfirmPassword(e.target.value)
                    }
                    placeholder={formatMessage({
                      id: "confirmPassword",
                      defaultMessage: "Confirm Password",
                    })}
                    isDisabled={isSubmitting}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowConfirm((v) => !v)}
                      tabIndex={-1}
                    />
                  </InputRightElement>
                </InputGroup>
                {confirmInvalid && (
                  <FormErrorMessage>
                    {formatMessage({
                      id: "bioflow.password.mismatch",
                      defaultMessage: "Passwords do not match.",
                    })}
                  </FormErrorMessage>
                )}
              </FormControl>

              <Box textAlign="center" pt={2}>
                <Button
                  colorScheme="messenger"
                  type="submit"
                  minWidth="120px"
                  isLoading={isSubmitting}
                  isDisabled={!canSubmit && touched}
                >
                  {formatMessage({
                    id: "bioflow.password.continue",
                    defaultMessage: "Continue",
                  })}
                </Button>
              </Box>
            </VStack>
          </FormControl>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BioFlowPasswordModal;
